import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Skeleton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';

import axiosInstance from '../../axiosInstance';

const PAS = 10;

/** « Il y a 20 min » se lit plus vite qu'une date, tant que c'est récent. */
function depuis(valeur) {
  if (!valeur) return '—';
  const date = new Date(valeur);
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "À l'instant";
  if (minutes < 60) return `Il y a ${minutes} min`;
  if (minutes < 1440) return `Il y a ${Math.floor(minutes / 60)} h`;
  return date.toLocaleString('fr-BE',
    { day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit' });
}

const quand = (log) => log.timestamp || log.created_at;

/** L'historique des dossiers consultés. */
function RequestLogs({ onUpdateStats }) {
  const naviguer = useNavigate();
  const [logs, setLogs] = useState([]);
  const [limite, setLimite] = useState(PAS);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const { data } = await axiosInstance.get('/requestlog/');
      const liste = [...(data.results || data)].sort(
        (a, b) => new Date(quand(b)) - new Date(quand(a)));
      setLogs(liste);
      // `data.length` était lu sur une réponse paginée : le compteur du
      // tableau de bord restait vide.
      onUpdateStats?.({ recentConsultations: data.count ?? liste.length });
    } catch {
      setErreur("L'historique n'a pas pu être chargé.");
    } finally {
      setChargement(false);
    }
  }, [onUpdateStats]);

  useEffect(() => { charger(); }, [charger]);

  const visibles = logs.slice(0, limite);
  const reste = logs.length - limite;

  if (chargement && logs.length === 0) {
    return <Stack spacing={1}>{[...Array(5)].map((_, i) => (
      <Skeleton key={i} height={40} />
    ))}</Stack>;
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center',
                                               justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          {logs.length} consultation{logs.length > 1 ? 's' : ''}
        </Typography>
        <Button size="small" onClick={charger} disabled={chargement}>
          Actualiser
        </Button>
      </Stack>

      {erreur && <Alert severity="error">{erreur}</Alert>}

      {logs.length === 0 ? (
        <Alert severity="info">Aucune consultation enregistrée.</Alert>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Expertise consultée</TableCell>
                  <TableCell>Quand</TableCell>
                  <TableCell align="right">&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibles.map((log) => {
                  const cible = log.expertise_id || log.expertise || log.id;
                  const recent = new Date(quand(log))
                    > new Date(Date.now() - 3600000);
                  return (
                    <TableRow key={log.id} hover sx={{ cursor: 'pointer' }}
                              onClick={() => naviguer(`/edit-expertise/${cible}`)}>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {log.title || log.path || 'Sans titre'}
                          </Typography>
                          {recent && <Chip size="small" color="success"
                                           label="Récent" />}
                        </Stack>
                        {log.title && log.path && (
                          <Typography variant="caption" color="text.secondary">
                            {log.path}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {depuis(quand(log))}
                        {log.user && (
                          <Typography variant="caption" color="text.secondary"
                                      component="div">
                            {log.user}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  naviguer(`/edit-expertise/${cible}`);
                                }}>
                          Ouvrir
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {(reste > 0 || limite > PAS) && (
            <Box sx={{ textAlign: 'center' }}>
              {reste > 0 && (
                <Button size="small" onClick={() => setLimite(limite + PAS)}>
                  Afficher {Math.min(reste, PAS)} de plus
                </Button>
              )}
              {limite > PAS && (
                <Button size="small" onClick={() => setLimite(PAS)}>
                  Réduire
                </Button>
              )}
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}

export default RequestLogs;
